import React, {useEffect, useContext} from "react"
import {
    BannerColumn,
    BannerRow,
    SideImageContainer,
    SideComponentsContainer,
    SearchBannerPadding,
    RegionNotFound,
    StyledSBReadmoreButton,
    PillsContainer,
} from "./components"
import {SearchBannerContext, useUniqueKey, INTEL_RECS} from "../utils"
import {type AnyRegion} from "./models"
import {type ComponentKey, getSearchBannerComponent} from "./utils"
import StructuredBrowsingChips from "../../structuredBrowsingChips"

interface RegionWrapperProps {
    hasSideImage: boolean
    images: any[]
    components: any[]
    strips: any[]
    copyOnly: boolean
    imageOnly: boolean
    hasCopy: boolean
    hasStructuredBrowsing: boolean
}

const StructuredBrowsingReadMore = ({hasStructuredBrowsing}: {hasStructuredBrowsing: boolean}) => {
    const {isExpanded, toggleIsExpanded, handleShowChevron} = useContext(SearchBannerContext)

    useEffect(() => {
        handleShowChevron(hasStructuredBrowsing)
        return () => {
            handleShowChevron(false)
        }
    }, [hasStructuredBrowsing, handleShowChevron])

    return (
        <PillsContainer>
            {hasStructuredBrowsing && (
                <StyledSBReadmoreButton
                    variant="text"
                    size="small"
                    onClick={toggleIsExpanded}
                    disableRipple
                    data-testid="structured-browsing-read-more"
                >
                    {isExpanded ? "- Read less" : "+ Read more"}
                </StyledSBReadmoreButton>
            )}
            <StructuredBrowsingChips show={isExpanded} />
        </PillsContainer>
    )
}

export const RegionWrapper = ({
    hasSideImage,
    images,
    components,
    strips,
    imageOnly,
    copyOnly,
    hasCopy,
    hasStructuredBrowsing,
}: RegionWrapperProps) => {
    return hasSideImage ? (
        <>
            <BannerRow>
                <SideImageContainer>
                    <Regions regions={images} />
                </SideImageContainer>
                <SideComponentsContainer>
                    <SearchBannerPadding $copyOnly={copyOnly} $imageOnly={imageOnly} />
                    {!hasCopy && hasStructuredBrowsing && (
                        <StructuredBrowsingReadMore hasStructuredBrowsing={hasStructuredBrowsing} />
                    )}
                    <Regions regions={components} />
                </SideComponentsContainer>
            </BannerRow>
            <Regions regions={strips} />
        </>
    ) : (
        <>
            <BannerColumn>
                <Regions regions={images} />
                <SearchBannerPadding key="full-width-component-wrapper" $copyOnly={copyOnly} $imageOnly={imageOnly} />
                {!hasCopy && hasStructuredBrowsing && (
                    <StructuredBrowsingReadMore hasStructuredBrowsing={hasStructuredBrowsing} />
                )}
                <Regions regions={components} />
            </BannerColumn>
            <Regions regions={strips} />
        </>
    )
}

export interface RegionProps {
    regions: AnyRegion<ComponentKey>[]
}

export const Regions = ({regions}: RegionProps) => {
    const regionKeys = useUniqueKey(regions.length)

    return (
        <>
            {regions.map((region, idx) => {
                const regionType = region?.type
                const Region = getSearchBannerComponent(regionType)

                if (!Region) {
                    return (
                        <RegionNotFound key={`${regionType}-${regionKeys[idx]}`}>
                            Cannot find region for {regionType}
                        </RegionNotFound>
                    )
                }

                return (
                    <Region
                        content={region.content}
                        overrideStyles={region?.overrideStyles}
                        key={`${regionType}-${regionKeys[idx]}`}
                        isIntelRecs={region?.dataSource === INTEL_RECS}
                    />
                )
            })}
        </>
    )
}
